import React from 'react';
import classNames from 'classnames';
import { isTablet } from 'react-device-detect';
import IPfArticleTile from './pfArticleTile.d';
import styles from '../../articleTileGrid/article-tile/article-tile.scss';
import PfTileImage from './pfTileImage/pfTileImage';
import PfTileDetails from './pfTileDetails/pfTileDetails';
import PercentageMatchEyecatcher from '../percentageEyecatcher/percentageMatchEyecatcher';
import FilterMatchEyecatcher from '../filterMatchEyecatcher/filterMatchEyecatcher';
import FilterMatchOverview from '../filterMatchOverview/filterMatchOverview';
import TileVariants from '../../articleTileGrid/article-tile/variants/tileVariants';
import { MatchEyecatcherType } from '../../../Common/enums';
import { IProductVariant } from '../productfinderresultpage.d';
import TabNavigationHelper from '../../../Common/tabNavigationHelper';

function PfArticleTile(props: IPfArticleTile) {
    const tileRef = React.useRef<HTMLDivElement>();
    const getImageUrl = (initialVariant: IProductVariant) => {
        let result = initialVariant.mainImage;

        if (initialVariant.mainImageGenericUrl) {
            result = initialVariant.mainImageGenericUrl.replace('--app-name--', 'ats');
        }
        return result;
    }
    const { product, isMobile, isGlobal, l10n, priceMode, finderType } = props;
    const pdpLink = product.variants[0].detailsPageLink;
    const [on_hover, setOnHover] = React.useState(false);
    const [isTouchAction, setIstouchAction] = React.useState(false);
    const [isTouchMove, setIsTouchMove] = React.useState(false);
    const [selectedColorCode, setSelectedColorCode] = React.useState(product.colorCode);
    const initialVariantIndex = React.useMemo(() => {
        const variantIndex = product.variants.findIndex(variant => variant.colorCode === product.colorCode);
        return variantIndex > -1 ? variantIndex : 0;
    }, []);

    const initialVariant = product.variants[initialVariantIndex];

    const [imageUrl, setImageUrl] = React.useState(getImageUrl(initialVariant));
    const [colorName, setColorName] = React.useState(initialVariant.title);
    let hoverStartupTimer: ReturnType<typeof setTimeout>;

    const handleTileClick = React.useCallback((event) => {
        if (isTouchAction) {
            event.preventDefault();
            setIstouchAction(false);
        }
        openOrderModal(filterOverViewContainerId);
    }, [product, selectedColorCode]);

    const openHoverContentByTouch = React.useCallback(() => {
        if (isMobile || !isTouchAction)
            return;

        if (on_hover && !isTouchMove)
            window.location.href = pdpLink;
        else if (!isTouchMove) {
            setOnHover(true);
        }

        // if it was not movement, on next click pdp will be opened
        setIsTouchMove(false);
    }, [isTouchMove, isTouchAction, on_hover]);

    const onMouseEnter = React.useCallback(() => {
        clearTimeout(hoverStartupTimer);
        hoverStartupTimer = setTimeout(() => {
            setOnHover(true);
        }, 750);
    }, []);

    const onMouseLeave = React.useCallback(() => {
        clearTimeout(hoverStartupTimer);
        setOnHover(false);
    }, []);

    const colorVariantsAmount = React.useMemo((): number => {
        const knownColorCodes = [];
        product.variants.forEach((variant) => {
            if (knownColorCodes.indexOf(variant.colorCode) === -1) {
                knownColorCodes.push(variant.colorCode);
            }
        });
        return knownColorCodes.length;
    }, [product]);
    const filterOverViewContainerId =
        `filter-overview-container-${product.masterArticle}-${product.salesArticleNo}-${product.colorCode}`;

    const openOrderModal = React.useCallback((contentContainerId) => {
        if (typeof window !== 'undefined') {
            const variant = product.variants.find((variant) => variant.colorCode === selectedColorCode) ||
                product.variants[0];
            
            const payload = {
                salesArticleNo: variant.salesArticleNo,
                masterArticleNo: Number(product.masterArticle),
                colorCode: variant.colorCode,
                sizeCode: null,
                itemOrigin: finderType,
                contentContainerId: contentContainerId,
                viewType: isMobile || isTablet ? 'sidepanel' : 'modal',
            };
            window.shell.publishTo('ESPP.OrderModal.Open', payload);
        }
    }, [product, selectedColorCode]);

    const changeColorVariant = (colorCode: number) => {
        const selectedVariant = product.variants.find(variant => variant.colorCode === colorCode);
        const selectedImageUrl = getImageUrl(selectedVariant);
        setSelectedColorCode(colorCode);
        setColorName(selectedVariant.title)
        setImageUrl(selectedImageUrl);
    }

    const shouldShowColorVariants = on_hover && product.variants && product.variants.length > 1;

    const handleKeyDown = React.useCallback((event) => {
        if (event.key === ' ' || event.key === 'Enter') { // space btn
            event.preventDefault();

            if(!on_hover && (product.variants && product.variants.length > 1)) {
                setOnHover(true);
            } else {
                openOrderModal(filterOverViewContainerId);
            }
        }

        if (event.key === 'Escape') { // space btn
            event.preventDefault();
            onMouseLeave();
            window.shell.tabNav.focus(tileRef.current, 'keyboard');
        }

    }, [on_hover, product]);

    React.useEffect(() => {
        if (tileRef.current)
            TabNavigationHelper.instance.setFocusAtt(tileRef.current);
    }, []);
    
    return (
        <div ref={tileRef}
            className={classNames(styles.article_tile, styles.pf_result)}
            data-testid='pf_article_tile' data-semantic-id='pf_article_tile'
            onMouseEnter={!isMobile ? onMouseEnter : undefined}
            onMouseLeave={!isMobile ? onMouseLeave : undefined}
            onKeyDown={handleKeyDown}
        >
            <div className={styles.idv_container}>
                <div
                    className={styles.tile_image_container}
                    onClick={handleTileClick}
                    onTouchMove={() => {
                        setIsTouchMove(true);
                    }}
                    onTouchEnd={openHoverContentByTouch}
                    onTouchStart={isMobile ? () => { return false; } : () => {
                        setIstouchAction(true);
                    }}
                >
                    <PfTileImage image={imageUrl} on_hover={on_hover} 
                    showAvailableSizes={false} articleName={product.description} 
                    colorName={colorName} mainViewMlt={l10n.mainView}/>
                    <PercentageMatchEyecatcher
                        percentagePoints={product.scorePercentage}
                        eyecatcherType={MatchEyecatcherType.percentage} />
                    <FilterMatchEyecatcher
                        matchingFilters={product.conditionsMatch}
                        nonMatchingFilters={product.conditionsNotMatch}
                        onPanelClick={() => openOrderModal(filterOverViewContainerId)}
                        isMobile={isMobile}
                    />
                </div>
                {/*TODO: ESPP-8555 - add ButtonColumn?*/}
                {/*<ButtonColumn category={category} variant={defaultColorVariant}*/}
                {/*              on_hover={on_hover} id={defaultSalesArticleNo}/>*/}

                <div
                    className={classNames(styles.dv_container)}
                    onClick={!shouldShowColorVariants ? handleTileClick : null}
                >
                    <PfTileDetails
                        description={product.description}
                        priceInformation={product.priceInformation}
                        l10n={l10n}
                        colorVariantsAmount={colorVariantsAmount}
                        on_hover={shouldShowColorVariants && !isTouchAction}
                        priceMode={priceMode}
                        isMobile={isMobile}
                        isGlobal={isGlobal}
                    />
                    {shouldShowColorVariants &&
                        <TileVariants
                            product={product}
                            tileIsHovering={on_hover && !isTouchAction}
                            changeColorVariant={changeColorVariant}
                            selectedColor={selectedColorCode}
                            pfResultColorClick={() => openOrderModal(filterOverViewContainerId)}
                            showAvailableSizesMenu={false}
                        />
                    }
                </div>
            </div>
            <div id={filterOverViewContainerId} className={styles.filter_overview_container}>
                <FilterMatchOverview
                    matchingFilters={product.conditionsMatch}
                    nonMatchingFilters={product.conditionsNotMatch}
                    percentagePoints={product.scorePercentage}
                    l10n={l10n}
                />
            </div>
        </div>
    );
}

export default PfArticleTile;

